import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SafeMarkdown } from '@/components/SafeMarkdown'
import { ProductStrategy, GenericProductInfo } from '../../redeem/productStrategy'
import { PromotionConfig } from '@/constants/UiPropertyKeys'
import { eventTracker, trackPurchaseCompleted } from '@/config/trackingFunctions'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { parsePeriod } from './utils/pricing.utils'
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { firebaseConfiguration } from '@/config/analytics'
import { getValue } from 'firebase/remote-config'
import { defaultFirebaseConfigurations } from '@/config/defaultFirebaseConfiguration'
import {
  AppShell,
  AppButton,
  AppCard,
  CheckoutShell,
} from '@/pages/external/components/shell'

// Cache Stripe promise so it's only created once per page lifecycle
let stripePromiseCache: Promise<Stripe | null> | null = null
const getStripePromise = () => {
  if (!stripePromiseCache) {
    let stripeApiKey: string
    try {
      const remoteKey = getValue(firebaseConfiguration, 'stripeApiKey').asString()
      stripeApiKey = remoteKey || defaultFirebaseConfigurations.stripeApiKey
    } catch {
      stripeApiKey = defaultFirebaseConfigurations.stripeApiKey
    }
    stripePromiseCache = loadStripe(stripeApiKey)
  }
  return stripePromiseCache
}

interface GenericProductListProps {
  productStrategies: ProductStrategy[]
  locale?: string
  base64ClientKey?: string
  promotionId?: string
  promotionConfig?: PromotionConfig
  onProductSelect?: (strategy: ProductStrategy, index: number) => void
  className?: string
}

/**
 * Themed product picker. Works with both DuprProducts (Stripe) and RevenueCat
 * Packages via the strategy pattern.
 *
 * Layout: AppShell + two-column on desktop. Left column: hero panel and the
 * promotion narrative. Right column: product cards, terms, and the CTA.
 */
export const GenericProductList: React.FC<GenericProductListProps> = ({
  productStrategies,
  locale = 'en-US',
  base64ClientKey,
  promotionId,
  promotionConfig,
  onProductSelect,
  className = ''
}) => {
  const { t } = useTranslation()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)

  const genericProducts: GenericProductInfo[] = productStrategies
    .filter((strategy) => strategy != null)
    .map((strategy) => {
      try {
        return strategy.toGenericProduct()
      } catch (err) {
        console.error('Error converting strategy to generic product:', err, strategy)
        return null
      }
    })
    .filter((product): product is GenericProductInfo => product != null)

  const handleSelectionChange = (index: number) => {
    if (index >= 0 && index < productStrategies.length) {
      setSelectedIndex(index)
      if (onProductSelect) {
        onProductSelect(productStrategies[index], index)
      }
    }
  }

  const handlePurchase = async () => {
    if (!productStrategies[selectedIndex]) {
      console.error('No product selected or invalid index')
      setError(t('errors.noProductSelected', 'No product selected'))
      return
    }

    const strategy = productStrategies[selectedIndex]
    const trackingData = strategy.getTrackingData()

    setIsLoading(true)
    setError(null)

    try {
      eventTracker(TRACKING_TYPES.PAYMENT_STARTED, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })

      const result = await strategy.purchase()

      if (typeof result === 'string') {
        setStripeClientSecret(result)
        return
      }

      eventTracker(TRACKING_TYPES.PAYMENT_COMPLETED_SUCCESS, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })
      trackPurchaseCompleted({
        purchase_flow: 'redeem',
        product_id: trackingData.product_id,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
      })

    } catch (err) {
      console.error('Purchase failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Purchase failed'
      setError(errorMessage)

      eventTracker(TRACKING_TYPES.PAYMENT_FAILED, {
        ...trackingData,
        partner_id: base64ClientKey,
        promotion_id: promotionId,
        error: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }

  const formatPrice = (price: number, currency: string = 'USD'): string => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price)
    } catch {
      return `$${price.toFixed(2)}`
    }
  }

  const calculateOriginalPrice = (promoPrice: number, discountPercent: number): number => {
    return promoPrice / (1 - discountPercent / 100)
  }

  // Stripe checkout secret returned — host inside CheckoutShell
  if (stripeClientSecret) {
    return (
      <CheckoutShell onBack={() => setStripeClientSecret(null)}>
        <EmbeddedCheckoutProvider
          stripe={getStripePromise()}
          options={{ clientSecret: stripeClientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </CheckoutShell>
    )
  }

  if (error) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[color:var(--negative)]/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-[color:var(--negative)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
              {t('errors.purchaseError', 'Purchase Error')}
            </h2>
            <p className="text-[color:var(--ink-soft)] mb-6">{error}</p>
            <AppButton
              onClick={() => {
                setError(null)
                setStripeClientSecret(null)
              }}
            >
              {t('common.tryAgain', 'Try Again')}
            </AppButton>
          </div>
        </div>
      </AppShell>
    )
  }

  if (!genericProducts.length) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[color:var(--surface-soft)] flex items-center justify-center">
              <svg className="w-6 h-6 text-[color:var(--ink-faint)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 009.586 13H7" />
              </svg>
            </div>
            <h2 className="text-xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
              {t('products.noProductsAvailable', 'No Products Available')}
            </h2>
            <p className="text-[color:var(--ink-soft)]">
              {t('products.checkBackLater', 'Please check back later.')}
            </p>
          </div>
        </div>
      </AppShell>
    )
  }

  const selectedProduct = genericProducts[selectedIndex]
  const benefits = promotionConfig?.benefits ?? []

  const headingTitle = promotionConfig?.title ? (
    <SafeMarkdown>{promotionConfig.title}</SafeMarkdown>
  ) : (
    'Choose your plan'
  )

  return (
    <AppShell logoLinksToDashboard={false} className={className}>
      {/* Centered content column. The picker is the centerpiece; the hero
           sits to its left at desktop sizes as a smaller decorative sidebar.
           max-w-5xl prevents the layout from stretching on ultrawide
           displays where the 55/45 split made the hero feel oversized.
           Vertical padding is kept tight so the page rarely needs more
           than the natural viewport height. */}
      <div className="mx-auto w-full max-w-5xl px-6 lg:px-8 py-6 md:py-10 lg:flex lg:items-start lg:justify-center lg:gap-x-12">
        {/* Decorative hero (desktop ≥1025px only). Not sticky — the whole
             page scrolls together with the now-scrollable nav. */}
        <aside className="hidden lg:block lg:w-80 lg:flex-shrink-0">
          <div className="rounded-[var(--radius-card)] bg-[color:var(--surface-soft)] flex flex-col items-center p-8 gap-y-5 overflow-hidden">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--ink-faint)]">
              DUPR+
            </p>
            <div
              className="
                w-40 h-40
                rounded-full overflow-hidden
                ring-1 ring-[color:var(--border-strong)]
                bg-[color:var(--surface)]
              "
            >
              <img
                src="/img/Dupr-Athletes/Ben_Action.png"
                alt=""
                className="w-full h-full object-cover object-top"
                aria-hidden
              />
            </div>
            <h2 className="text-xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] leading-tight text-center max-w-[18ch]">
              {headingTitle}
            </h2>
            {benefits.length > 0 && (
              <ul className="space-y-1.5 self-stretch mt-1">
                {benefits.slice(0, 3).map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[color:var(--ink-soft)]">
                    <svg className="w-3.5 h-3.5 text-[color:var(--positive)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <SafeMarkdown>{benefit}</SafeMarkdown>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Picker — the centerpiece. Constrained to a mobile-friendly
             width (max-w-sm ≈ 384px) so the layout never feels wider than
             a phone browser. Centered horizontally below lg, sits beside
             the hero at lg+. */}
        <main className="w-full lg:max-w-sm lg:flex-shrink-0 mx-auto lg:mx-0">
          <p className="text-xs uppercase tracking-widest text-[color:var(--ink-faint)] mb-2 lg:hidden">
            DUPR+
          </p>
          <h1 className="text-2xl md:text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
            {headingTitle}
          </h1>
          {promotionConfig?.instructions && (
            <p className="text-sm md:text-base text-[color:var(--ink-soft)] mb-5 max-w-[55ch]">
              <SafeMarkdown>{promotionConfig.instructions}</SafeMarkdown>
            </p>
          )}

            {/* Product cards */}
            <div className="space-y-3">
              {genericProducts.map((product, index) => {
                const period = parsePeriod(product.period || 'P1M')
                const isSelected = selectedIndex === index

                return (
                  <AppCard
                    key={product.id}
                    interactive
                    selected={isSelected}
                    onClick={() => handleSelectionChange(index)}
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleSelectionChange(index)
                      }
                    }}
                    /* Fixed height so cards don't change size based on which
                       inner variation (trial / discount / sponsor chip) is
                       present. Internal sections fill the available space. */
                    className="p-5 min-h-[8.5rem] flex flex-col gap-y-2"
                  >
                    {/* Row 1: title + badges */}
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="font-semibold text-[color:var(--ink)] leading-tight">{product.displayName}</h3>
                      <div className="flex gap-2 flex-wrap justify-end">
                        {Number(product.trialDays) > 0 && (
                          <span className="inline-flex items-center gap-1 bg-[color:var(--positive)]/10 text-[color:var(--positive)] text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {product.trialDays}d free
                          </span>
                        )}
                        {Number(product.priceReduction) > 0 && (
                          <span className="inline-flex items-center gap-1 bg-[color:var(--warning)]/15 text-[color:var(--warning)] text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {product.priceReduction}% off
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Row 2: period + price (flex-1 so it absorbs any
                         leftover height in the fixed-size card). */}
                    <div className="flex justify-between items-center gap-3 flex-1">
                      <p className="text-sm text-[color:var(--ink-soft)] capitalize">
                        {product.isOneTime ? 'One-time purchase' : period.displayNameLong}
                      </p>
                      {product.isOneTime ? (
                        <p className="text-2xl font-semibold text-[color:var(--ink)]">
                          {formatPrice(product.price, product.currency)}
                        </p>
                      ) : Number(product.trialDays) > 0 ? (
                        <div className="flex flex-col items-end leading-tight">
                          <p className="text-lg font-semibold text-[color:var(--positive)]">Free today</p>
                          {Number(product.priceReduction) > 0 && (
                            <p className="text-xs text-[color:var(--ink-faint)] line-through">
                              {formatPrice(calculateOriginalPrice(product.price, product.priceReduction || 0), product.currency)}/{period.displayName}
                            </p>
                          )}
                          <p className="text-xs text-[color:var(--ink-soft)]">
                            then {formatPrice(product.price, product.currency)}/{period.displayName}
                          </p>
                        </div>
                      ) : Number(product.priceReduction) > 0 ? (
                        <div className="flex flex-col items-end leading-tight">
                          <p className="text-sm text-[color:var(--ink-faint)] line-through">
                            {formatPrice(calculateOriginalPrice(product.price, product.priceReduction || 0), product.currency)}/{period.displayName}
                          </p>
                          <p className="text-2xl font-semibold text-[color:var(--ink)]">
                            {formatPrice(product.price, product.currency)}/{period.displayName}
                          </p>
                        </div>
                      ) : (
                        <p className="text-2xl font-semibold text-[color:var(--ink)]">
                          {formatPrice(product.price, product.currency)}/{period.displayName}
                        </p>
                      )}
                    </div>

                    {/* Row 3: feature/sponsor chips. Slot is rendered always
                         (even empty) so cards with and without sponsor
                         chips are the same overall height. */}
                    <div className="min-h-[1.25rem] flex flex-wrap gap-1.5 items-center">
                      {product.features?.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="text-[11px] text-[color:var(--ink-soft)] bg-[color:var(--surface-soft)] px-2 py-0.5 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </AppCard>
                )
              })}
            </div>

            {/* Terms / billing disclosure */}
            {selectedProduct && (
              <div className="mt-4 text-[11px] leading-snug text-[color:var(--ink-soft)] text-center">
                {selectedProduct.isOneTime ? (
                  <span>One-time payment of {formatPrice(selectedProduct.price, selectedProduct.currency)}.</span>
                ) : Number(selectedProduct.trialDays) > 0 ? (
                  <>
                    <span>
                      {selectedProduct.trialDays} day{selectedProduct.trialDays !== 1 ? 's' : ''} free trial,
                      then {formatPrice(selectedProduct.price, selectedProduct.currency)}/{parsePeriod(selectedProduct.period || 'P1M').displayName}.
                    </span>{' '}
                    <span>Auto-renews until canceled. Cancel anytime.</span>
                  </>
                ) : (
                  <>
                    <span>
                      Billed {formatPrice(selectedProduct.price, selectedProduct.currency)}/{parsePeriod(selectedProduct.period || 'P1M').displayName}.
                    </span>{' '}
                    <span>Auto-renews until canceled. Cancel anytime.</span>
                  </>
                )}
                <div className="mt-1.5">
                  By {selectedProduct.isOneTime ? 'purchasing' : 'subscribing'} you agree to our{' '}
                  <a
                    href="https://www.dupr.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[color:var(--ink)] transition-colors"
                  >
                    Terms
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.dupr.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[color:var(--ink)] transition-colors"
                  >
                    Privacy Policy
                  </a>
                  .
                </div>
                {promotionConfig?.footerText && (
                  <div className="mt-2 text-[color:var(--ink-faint)]">
                    <SafeMarkdown>{promotionConfig.footerText}</SafeMarkdown>
                  </div>
                )}
              </div>
            )}

          <AppButton
            fullWidth
            className="mt-4"
            onClick={handlePurchase}
            loading={isLoading}
            disabled={!selectedProduct}
          >
            {isLoading ? t('common.processing', 'Processing...') : t('common.startNow', 'Start Now')}
          </AppButton>
        </main>
      </div>
    </AppShell>
  )
}

export default GenericProductList
